import { getModule, Module, Mutation, MutationAction, VuexModule } from 'vuex-module-decorators';
import { FlagGroup, UserFlag } from '~/interfaces/user.interface';
import apiService from '~/services/api.service';
import store from '~common-store';
import { PopupModule } from '~common-store/modules/popup';
import { NotificationsModule } from '~common/store/modules/notifications';
import { UserModule } from './user';

@Module({ dynamic: true, store, name: 'remoteFeatureFlags', namespaced: true })
class RemoteFeatureFlags extends VuexModule {
  availableFlags: UserFlag[] = [];
  currentUserFlagIds: string[] = [];
  currentUserGroupIds: string[] = [];
  availableGroups: FlagGroup[] = [];
  currentRolloutValue: number = 0;
  currentRolloutFlag: UserFlag | null = null;

  get currentUserFlags(): UserFlag[] {
    return this.availableFlags.filter((flag) => this.currentUserFlagIds.includes(flag.id));
  }

  get currentUserGroups(): FlagGroup[] {
    return this.availableGroups.filter((group) => this.currentUserGroupIds.includes(group.id));
  }

  /**
   * @description Get all flags that are inherited from groups.
   */
  get currentUserGroupFlags(): UserFlag[] {
    return this.availableFlags.filter((flag) =>
      this.currentUserGroups.flatMap((group) => group.flags.map((flag) => flag.id)).includes(flag.id),
    );
  }

  /**
   * @description Get all flags not inherited from groups.
   */
  get currentUserStandaloneFlags(): UserFlag[] {
    return this.currentUserFlags.filter((flag) => {
      return !this.currentUserGroupFlags.some((groupFlag) => groupFlag.id === flag.id);
    });
  }

  @Mutation
  openRolloutModal(flag: UserFlag): void {
    this.currentRolloutFlag = flag;
    this.currentRolloutValue = flag.progress ?? 0;
    PopupModule.setPopupRequested('rollout');
  }

  @Mutation
  setCurrentRolloutValue(value: number): void {
    this.currentRolloutValue = value;
  }

  @MutationAction
  async addFlagToUser(flagId: string): Promise<{ currentUserFlagIds: string[] }> {
    try {
      const response = await apiService.adduserflag({ user_id: UserModule.id, flag_id: flagId });
      const data = response?.data.body.target.flags as UserFlag[];
      const flagIds = data.map((flag) => flag.id) as string[];
      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag added to user',
        type: 'success',
      });

      return { currentUserFlagIds: flagIds };
    } catch (error) {
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Could not add flag to user',
        type: 'error',
      });
      return { currentUserFlagIds: [] };
    }
  }

  @MutationAction
  async removeFlagFromUser(flagId: string): Promise<{ currentUserFlagIds: string[] }> {
    try {
      const response = await apiService.removeuserflag({ user_id: UserModule.id, flag_id: flagId });
      const data = response?.data.body.target.flags as UserFlag[];
      const flagIds = data.map((flag) => flag.id) as string[];
      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag removed from user',
        type: 'success',
      });
      return { currentUserFlagIds: flagIds };
    } catch (error) {
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Could not remove flag from user',
        type: 'error',
      });
      return { currentUserFlagIds: [] };
    }
  }

  @MutationAction
  async getAvailableFlags(): Promise<{ availableFlags: UserFlag[] }> {
    try {
      const response = await apiService.getavailableflags();
      const data = response?.data.body.flags as UserFlag[];
      const flags = data.map((flag) => {
        return {
          id: flag.id,
          name: flag.name || '',
          description: flag.description || '',
          created_at: flag.created_at,
          expire_at: flag.expire_at,
          released: flag.released || false,
          progress: flag.progress,
        };
      });

      return { availableFlags: flags };
    } catch (error) {
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Could not fetch available flags',
        type: 'error',
      });
      return { availableFlags: [] };
    }
  }

  @MutationAction
  async getCurrentUserFlagIds(): Promise<{ currentUserFlagIds: string[]; currentUserGroupIds: string[] }> {
    try {
      const response = await apiService.getuserflags({ user_id: UserModule.id });
      const flags = response?.data.body.target.flags as UserFlag[];
      const groups = response?.data.body.target.groups as FlagGroup[];
      return { currentUserFlagIds: flags.map((flag) => flag.id), currentUserGroupIds: groups.map((group) => group.id) };
    } catch (error) {
      return { currentUserFlagIds: [], currentUserGroupIds: [] };
    }
  }

  @MutationAction
  async createFlag(params: {
    name: string;
    description: string;
    expire_at: string;
  }): Promise<{ availableFlags: UserFlag[] } | undefined> {
    try {
      const response = await apiService.createflag({
        ...params,
        expire_at: new Date(params.expire_at).valueOf(),
      });
      const newFlag = response?.data.body.flag as UserFlag;

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag added',
        type: 'success',
      });
      return { availableFlags: [...RemoteFeatureFlagsModule.availableFlags, newFlag] };
    } catch (error) {
      console.error('Error adding flag:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to create remote feature flag. Please try again.',
        type: 'error',
      });
    }
  }

  @MutationAction
  async updateFlag(params: {
    name: string;
    flag_id: string;
    description: string;
    expire_at: string;
  }): Promise<{ availableFlags: UserFlag[] } | undefined> {
    try {
      const response = await apiService.updateflag({
        ...params,
        expire_at: new Date(params.expire_at).valueOf(),
      });
      const updatedFlag = response?.data.body.flag as UserFlag;

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag updated',
        type: 'success',
      });

      const flags = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableFlags)) as UserFlag[];

      const localFlag = flags.find((flag) => flag.id === updatedFlag.id);
      if (localFlag) {
        localFlag.description = updatedFlag.description;
        localFlag.expire_at = updatedFlag.expire_at;
      }

      return { availableFlags: flags };
    } catch (error) {
      console.error('Error updating flag:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to update remote feature flag. Please try again.',
        type: 'error',
      });
      throw error;
    }
  }

  @MutationAction
  async releaseProgressiveRolloutFlag(params: {
    flag: UserFlag;
    progressive: number;
  }): Promise<{ availableFlags: UserFlag[] } | undefined> {
    const { flag, progressive } = params;
    try {
      await apiService.releaseflag({ flag_id: flag.id, progressive: progressive });

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag updated',
        type: 'success',
      });

      const flags = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableFlags)) as UserFlag[];
      const localFlag = flags.find((currentFlag) => currentFlag.id === flag.id);

      if (localFlag) {
        localFlag.released = flag.released;
      }
      return { availableFlags: flags };
    } catch (error) {
      console.error('Error updating flag:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to release progressive rollout flag. Please try again.',
        type: 'error',
      });
      return { availableFlags: RemoteFeatureFlagsModule.availableFlags };
    }
  }

  @MutationAction
  async revertFlag(flag_id: string) {
    try {
      await apiService.revertflag({ flag_id: flag_id });

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag updated',
        type: 'success',
      });

      const flags = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableFlags)) as UserFlag[];
      const localFlag = flags.find((flag) => flag.id === flag_id);

      if (localFlag) {
        localFlag.released = false;
        localFlag.progress = null;
      }
      return { availableFlags: flags };
    } catch (error) {
      console.error('Error updating flag:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to revert feature flag. Please try again.',
        type: 'error',
      });
    }
  }

  @MutationAction
  async deleteFlag(flag_id: string): Promise<{ availableFlags: UserFlag[] } | undefined> {
    try {
      await apiService.deleteflag({ flag_id });
      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Flag deleted',
        type: 'success',
      });

      const flags = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableFlags)) as UserFlag[];
      const localFlag = flags.find((flag) => flag.id === flag_id);

      if (localFlag) {
        flags.splice(flags.indexOf(localFlag), 1);
      }
      return { availableFlags: flags };
    } catch (error) {
      console.error('Error deleting flag:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to delete remote feature flag. Please try again.',
        type: 'error',
      });
    }
  }

  @MutationAction
  async getAvailableGroups(): Promise<{ availableGroups: FlagGroup[] }> {
    try {
      const response = await apiService.getavailablegroups();
      const data = response?.data.body.groups as FlagGroup[];
      const groups = data.map((group) => {
        return {
          id: group.id,
          name: group.name || '',
          flags: group.flags || [],
          targets: group.targets || [],
          created_at: group.created_at,
          update_at: group.update_at,
        };
      });

      return { availableGroups: groups };
    } catch (error) {
      console.error('Error fetching groups:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Could not fetch available groups',
        type: 'error',
      });
      return { availableGroups: [] };
    }
  }

  @MutationAction
  async createGroup(params: { name: string }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    const response = await apiService.creategroup(params);
    const newGroup = { ...(response?.data.body.group as FlagGroup), flags: [], targets: [] };

    NotificationsModule.pushNotification({
      title: 'Success',
      text: 'Group created',
      type: 'success',
    });
    return { availableGroups: [...RemoteFeatureFlagsModule.availableGroups, newGroup] };
  }

  @MutationAction
  async updateGroup(params: { group_id: string; name: string }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    try {
      // Call the API endpoint
      const response = await apiService.updategroup({
        group_id: params.group_id,
        name: params.name,
      });

      const updatedGroup = response?.data.body.group as FlagGroup;
      const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
      const groupIndex = groups.findIndex((g) => g.id === params.group_id);

      if (groupIndex === -1) {
        throw new Error('Group not found');
      }
      // Update local state
      groups[groupIndex].name = updatedGroup.name;
      groups[groupIndex].update_at = Date.now();

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'Group updated',
        type: 'success',
      });

      return { availableGroups: groups };
    } catch (error) {
      console.error('Error updating group:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to update group. Please try again.',
        type: 'error',
      });
      throw error;
    }
  }

  @MutationAction
  async deleteGroup(group_id: string): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
    await apiService.deletegroup({ group_id });
    const filteredGroups = groups.filter((g) => g.id !== group_id);

    NotificationsModule.pushNotification({
      title: 'Success',
      text: 'Group deleted',
      type: 'success',
    });

    return { availableGroups: filteredGroups };
  }

  @MutationAction
  async addUserToGroup(params: {
    group_id: string;
    user_email: string;
  }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    // Get user_id from email via getusersinfo
    const userDataResponse = await apiService.getusersinfo({ user_mails: [params.user_email] });
    if (userDataResponse.data.body.users.length === 0) {
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'User not found',
        type: 'error',
      });
    }
    const user_id = userDataResponse?.data.body.users?.[0]?.id;

    if (!user_id) {
      throw new Error('User not found');
    }

    const updatedGroup = (await apiService.addgroupuser({ group_id: params.group_id, user_id })).data.body.group;

    const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
    const groupIndex = groups.findIndex((g) => g.id === params.group_id);

    groups[groupIndex].targets = updatedGroup.targets;

    NotificationsModule.pushNotification({
      title: 'Success',
      text: 'User added to group',
      type: 'success',
    });

    return { availableGroups: groups };
  }

  @MutationAction
  async removeUserFromGroup(params: {
    group_id: string;
    user_id: string;
  }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    try {
      const updatedGroup = (await apiService.removegroupuser({ group_id: params.group_id, user_id: params.user_id }))
        .data.body.group;

      const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
      const groupIndex = groups.findIndex((g) => g.id === params.group_id);

      if (groupIndex !== -1) {
        groups[groupIndex] = updatedGroup;
      }

      NotificationsModule.pushNotification({
        title: 'Success',
        text: 'User removed from group',
        type: 'success',
      });

      return { availableGroups: groups };
    } catch (error) {
      console.error('Error removing user from group:', error);
      NotificationsModule.pushNotification({
        title: 'Error',
        text: 'Failed to remove user from group. Please try again.',
        type: 'error',
      });
      throw error;
    }
  }

  @MutationAction
  async addFlagToGroup(params: {
    group_id: string;
    flag_id: string;
  }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    await apiService.addgroupflag({ group_id: params.group_id, flag_id: params.flag_id });

    const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
    const groupIndex = groups.findIndex((g) => g.id === params.group_id);

    if (groupIndex !== -1) {
      if (!groups[groupIndex].flags.map((flag) => flag.id).includes(params.flag_id)) {
        groups[groupIndex].flags.push({ id: params.flag_id });
        groups[groupIndex].update_at = Date.now();
      }
    }

    NotificationsModule.pushNotification({
      title: 'Success',
      text: 'Flag added to group',
      type: 'success',
    });

    return { availableGroups: groups };
  }

  @MutationAction
  async removeFlagFromGroup(params: {
    group_id: string;
    flag_id: string;
  }): Promise<{ availableGroups: FlagGroup[] } | undefined> {
    await apiService.removegroupflag({ group_id: params.group_id, flag_id: params.flag_id });

    const groups = JSON.parse(JSON.stringify(RemoteFeatureFlagsModule.availableGroups)) as FlagGroup[];
    const groupIndex = groups.findIndex((g) => g.id === params.group_id);

    if (groupIndex !== -1) {
      groups[groupIndex].flags = groups[groupIndex].flags.filter((flag) => flag.id !== params.flag_id);
      groups[groupIndex].update_at = Date.now();
    }

    NotificationsModule.pushNotification({
      title: 'Success',
      text: 'Flag removed from group',
      type: 'success',
    });

    return { availableGroups: groups };
  }
}

export const RemoteFeatureFlagsModule = getModule(RemoteFeatureFlags);
